import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { SUMMIT_CONFIG } from '../config/summitConfig';

interface AdvisorsSectionProps {
  reducedMotion: boolean;
}

export const AdvisorsSection: React.FC<AdvisorsSectionProps> = ({ reducedMotion }) => {
  return (
    <section 
      id="advisors" 
      className="relative py-24 sm:py-32 bg-gradient-to-b from-deepblack via-[#111111] to-deepblack overflow-hidden perspective-stage border-t border-white/5"
    >
      {/* Background Architectural Watermark */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02] flex items-center justify-center font-serif text-[22vw] font-bold text-beige select-none">
        MENTORS
      </div>

      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 relative z-10">
        
        {/* Section Pre-title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-[1px] bg-bronze" />
          <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
            ADVISORY BOARD • MENTORSHIP & GOVERNANCE
          </span>
          <div className="w-12 h-[1px] bg-bronze" />
        </div>

        {/* Section Heading & Subheading */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6">
          <div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ivory">
              MEET OUR <br />
              <span className="text-beige font-light italic">ADVISORS</span>
            </h2>
          </div>
          <div className="max-w-lg">
            <p className="text-sm text-ivory/70 font-light leading-relaxed">
              Distinguished academicians, institutional custodians, and visionary industry leaders guiding the academic rigor, structural integrity, and executive vision of {SUMMIT_CONFIG.conferenceName}.
            </p>
          </div>
        </div>

        {/* 3 Advisors Grid (3-column layout on desktop) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7 items-stretch">
          {SUMMIT_CONFIG.advisors.map((advisor) => (
            <div
              key={advisor.id}
              className="marble-card rounded-sm p-6 sm:p-7 flex flex-col h-full relative transition-all duration-300 border border-bronze/40 hover:border-beige/70 shadow-xl group"
              data-cursor-interactive
            >
              {/* Greek Key Header Trim */}
              <div className="w-full h-1 greek-key-border opacity-50 mb-5" />

              {/* Top Badge & Member Number */}
              <div className="flex items-center justify-between gap-2 mb-5">
                <span className="font-mono text-[10px] font-bold text-bronze tracking-widest px-2.5 py-0.5 bg-white/5 border border-bronze/30 rounded-sm uppercase">
                  MEMBER NO. {advisor.number}
                </span>
                <span className="text-[9px] uppercase tracking-widest text-beige flex items-center gap-1 font-semibold">
                  <ShieldCheck className="w-3 h-3 text-bronze" />
                  ADVISORY BOARD
                </span>
              </div>

              {/* Advisor Photo Frame */}
              <div className="relative aspect-[8/9] w-full overflow-hidden rounded-sm border border-bronze/40 p-1 bg-black/70 shadow-xl group-hover:border-beige/70 transition-colors mb-5">
                <div className="relative w-full h-full overflow-hidden rounded-sm bg-charcoal-subtle">
                  <img 
                    src={advisor.image} 
                    alt={advisor.name} 
                    className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105 filter brightness-95 contrast-105 group-hover:brightness-100"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-deepblack/70 via-transparent to-transparent opacity-60 pointer-events-none" />
                </div>
                {/* Classical Corner Accents */}
                <div className="absolute top-1.5 left-1.5 w-2.5 h-2.5 border-t-2 border-l-2 border-bronze pointer-events-none" />
                <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 border-t-2 border-r-2 border-bronze pointer-events-none" />
                <div className="absolute bottom-1.5 left-1.5 w-2.5 h-2.5 border-b-2 border-l-2 border-bronze pointer-events-none" />
                <div className="absolute bottom-1.5 right-1.5 w-2.5 h-2.5 border-b-2 border-r-2 border-bronze pointer-events-none" />
              </div>

              {/* Text Details Container */}
              <div className="flex-1 flex flex-col">
                {/* Name & Title */}
                <h3 className="font-serif text-xl sm:text-2xl font-bold tracking-wide text-ivory group-hover:text-beige transition-colors mb-1.5 leading-snug min-h-[3.5rem] flex items-center">
                  {advisor.name}
                </h3>
                <h4 className="text-[11px] uppercase tracking-widest text-beige-400 font-semibold mb-3.5 leading-relaxed min-h-[2.5rem]">
                  {advisor.title}
                </h4>

                {/* Affiliations / Pill Highlights */}
                <div className="flex flex-wrap gap-1.5 mb-4 pb-4 border-b border-white/5">
                  {advisor.affiliations.map((tag) => (
                    <span 
                      key={tag}
                      className="text-[9px] font-mono tracking-widest uppercase px-2 py-0.5 bg-white/5 border border-bronze/30 text-beige-300 rounded-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Bio Paragraphs */}
                <div className="space-y-2.5 text-xs text-ivory/75 font-light leading-relaxed mb-6">
                  {advisor.paragraphs.map((p, idx) => (
                    <p key={idx} className="leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </div>

              {/* Classical Pedestal Footing at bottom */}
              <div className="mt-auto pt-4 border-t border-bronze/30 flex items-center justify-between text-[9px] font-mono text-bronze uppercase">
                <span>FLSxMUN 2.0</span>
                <span>ADVISORY BOARD</span>
              </div>

            </div>
          ))}
        </div>

        {/* Bottom Classical Meander */}
        <div className="mt-14 w-full h-2 greek-key-border opacity-30" />

      </div>
    </section>
  );
};
